import { Box, Chip, Stack, Typography, Accordion, AccordionSummary, AccordionDetails, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MathContent from "../../../../../customComponents/MathContent";
import { useState } from "react";
import ObjectiveForm from "./ObjectiveForm";
import SubjectiveForm from "./SubjectiveForm";

const NewQuestion = () => {
  const [questionType, setQuestionType] = useState("objective");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 2, md: 4 } }}>
        <Typography sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, fontWeight: 600 }}>
          Create New Question
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip
            label="Objective"
            variant={questionType === "objective" ? "filled" : "outlined"}
            color={questionType === "objective" ? "primary" : "default"}
            onClick={() => setQuestionType("objective")}
            sx={{ fontWeight: 500, cursor: "pointer" }}
          />
          <Chip
            label="Subjective"
            variant={questionType === "subjective" ? "filled" : "outlined"}
            color={questionType === "subjective" ? "primary" : "default"}
            onClick={() => setQuestionType("subjective")}
            sx={{ fontWeight: 500, cursor: "pointer" }}
          />
        </Stack>
      </Box>

      <Accordion
        disableGutters
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${isDark ? "#1e3a5f" : "#bfdbfe"}`,
          borderRadius: "10px !important",
          bgcolor: isDark ? "#0d1f33" : "#eff6ff",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: isDark ? "#64b5f6" : "#1d4ed8" }} />}
          sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0 } }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: isDark ? "#64b5f6" : "#1d4ed8" }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#64b5f6" : "#1d4ed8" }}>
              How to write math equations
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
            <code style={{ background: isDark ? "#1a2e4a" : "#dbeafe", borderRadius: 4, padding: "1px 5px" }}>$...$</code> for inline or <code style={{ background: isDark ? "#1a2e4a" : "#dbeafe", borderRadius: 4, padding: "1px 5px" }}>$$...$$</code> for block display.
          </Typography>
          <Box
            component="table"
            sx={{ borderCollapse: "collapse", width: "100%", fontSize: "0.8rem" }}
          >
            <tbody>
              {[
                ["Fraction", "$\\frac{a}{b}$"],
                ["Power / Subscript", "$x^{2}$, $x_{1}$"],
                ["Square root", "$\\sqrt{x}$"],
                ["Greek letters", "$\\alpha$, $\\beta$, $\\pi$"],
                ["Trig functions", "$\\sin(x)$, $\\cos(x)$"],
                ["Multiplication dot", "$a \\cdot b$"],
                ["Summation", "$\\sum_{i=1}^{n} i$"],
              ].map(([label, math]) => (
                <Box
                  component="tr"
                  key={label}
                  sx={{ "&:nth-of-type(even) td": { bgcolor: isDark ? "#1a2e4a44" : "#dbeafe33" } }}
                >
                  <Box component="td" sx={{ py: 0.5, pr: 2, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {label}
                  </Box>
                  <Box component="td" sx={{ py: 0.5, pr: 2 }}>
                    <code style={{ background: isDark ? "#1a2a3a" : "#f1f5f9", borderRadius: 4, padding: "1px 5px" }}>{math}</code>
                  </Box>
                  <Box component="td" sx={{ py: 0.5, color: "text.secondary" }}>
                    <MathContent text={math} />
                  </Box>
                </Box>
              ))}
            </tbody>
          </Box>
        </AccordionDetails>
      </Accordion>

      {questionType === "objective" && <ObjectiveForm />}
      {questionType === "subjective" && <SubjectiveForm />}
    </Box>
  );
};

export default NewQuestion;
